/**
 * useApi - Backwards-compatible React Query hooks
 *
 * Migration: Change `from 'react-query'` → `from '@/hooks/useApi'`
 *
 * What this wrapper does:
 * - Re-adds onSuccess/onError/onSettled (removed in v5)
 * - Maps cacheTime → gcTime (renamed in v5)
 * - Maps keepPreviousData → placeholderData (changed in v5)
 * - Maps isPreviousData → isPlaceholderData (renamed in v5)
 * - Transforms refetchInterval callback signature (changed in v5)
 * - Wraps useInfiniteQuery to support 3-arg style and extract initialPageParam
 * - Converts string keys to arrays (required in v5)
 * - Provides isLoading = isPending && isFetching (v3 behavior)
 * - Supports both object style and 3-arg style
 */

import { useEffect, useRef, useMemo } from 'react';
import {
  useQuery as tanstackUseQuery,
  useMutation as tanstackUseMutation,
  useQueries as tanstackUseQueries,
  useQueryClient as tanstackUseQueryClient,
  useInfiniteQuery as tanstackUseInfiniteQuery,
  keepPreviousData as tanstackKeepPreviousData,
  QueryClient,
  QueryClientProvider,
  type UseQueryOptions,
  type UseQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type QueryKey,
  type QueryFunction,
} from '@tanstack/react-query';

// ─────────────────────────────────────────────────────────────
// Re-exports
// ─────────────────────────────────────────────────────────────
export { QueryClient, QueryClientProvider };
export { keepPreviousData } from '@tanstack/react-query';

// Normalize string keys to arrays (used by multiple functions)
const toArrayKey = (key: QueryKey | string | undefined): QueryKey | undefined =>
  typeof key === 'string' ? [key] : key;

/**
 * useQueryClient - Wrapped to normalize string keys to arrays
 * Supports legacy patterns like queryClient.getQueryData('key')
 */
export const useQueryClient = () => {
  const queryClient = tanstackUseQueryClient();

  return useMemo(() => ({
    ...queryClient,

    // getQueryData('key') → getQueryData(['key'])
    getQueryData: (key: QueryKey | string) => queryClient.getQueryData(toArrayKey(key)!),

    // setQueryData('key', data) → setQueryData(['key'], data)
    setQueryData: (key: QueryKey | string, updater: any) => queryClient.setQueryData(toArrayKey(key)!, updater),

    // getQueriesData('key') → getQueriesData({ queryKey: ['key'] })
    getQueriesData: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.getQueriesData({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.getQueriesData({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.getQueriesData({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.getQueriesData(filters);
    },

    // setQueriesData('key', updater) → setQueriesData({ queryKey: ['key'] }, updater)
    setQueriesData: (filters: any, updater: any) => {
      if (typeof filters === 'string') {
        return queryClient.setQueriesData({ queryKey: [filters] }, updater);
      }
      if (Array.isArray(filters)) {
        return queryClient.setQueriesData({ queryKey: filters }, updater);
      }
      if (filters?.queryKey) {
        return queryClient.setQueriesData({ ...filters, queryKey: toArrayKey(filters.queryKey) }, updater);
      }
      return queryClient.setQueriesData(filters, updater);
    },

    // invalidateQueries('key') → invalidateQueries({ queryKey: ['key'] })
    invalidateQueries: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.invalidateQueries({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.invalidateQueries({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.invalidateQueries({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.invalidateQueries(filters);
    },

    // prefetchQuery('key', fn) → prefetchQuery({ queryKey: ['key'], queryFn: fn })
    prefetchQuery: (arg1: any, arg2?: any, arg3?: any) => {
      if (typeof arg1 === 'string' || Array.isArray(arg1)) {
        return queryClient.prefetchQuery({
          queryKey: toArrayKey(arg1)!,
          queryFn: arg2,
          ...arg3,
        });
      }
      if (arg1?.queryKey) {
        return queryClient.prefetchQuery({ ...arg1, queryKey: toArrayKey(arg1.queryKey) });
      }
      return queryClient.prefetchQuery(arg1);
    },

    // refetchQueries('key') → refetchQueries({ queryKey: ['key'] })
    refetchQueries: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.refetchQueries({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.refetchQueries({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.refetchQueries({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.refetchQueries(filters);
    },

    // resetQueries('key') → resetQueries({ queryKey: ['key'] })
    resetQueries: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.resetQueries({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.resetQueries({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.resetQueries({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.resetQueries(filters);
    },

    // removeQueries('key') → removeQueries({ queryKey: ['key'] })
    removeQueries: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.removeQueries({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.removeQueries({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.removeQueries({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.removeQueries(filters);
    },

    // cancelQueries('key') → cancelQueries({ queryKey: ['key'] })
    cancelQueries: (filters?: any) => {
      if (typeof filters === 'string') {
        return queryClient.cancelQueries({ queryKey: [filters] });
      }
      if (Array.isArray(filters)) {
        return queryClient.cancelQueries({ queryKey: filters });
      }
      if (filters?.queryKey) {
        return queryClient.cancelQueries({ ...filters, queryKey: toArrayKey(filters.queryKey) });
      }
      return queryClient.cancelQueries(filters);
    },

    // fetchQuery('key', fn) → fetchQuery({ queryKey: ['key'], queryFn: fn })
    fetchQuery: (arg1: any, arg2?: any, arg3?: any) => {
      if (typeof arg1 === 'string' || Array.isArray(arg1)) {
        return queryClient.fetchQuery({
          queryKey: toArrayKey(arg1)!,
          queryFn: arg2,
          ...arg3,
        });
      }
      if (arg1?.queryKey) {
        return queryClient.fetchQuery({ ...arg1, queryKey: toArrayKey(arg1.queryKey) });
      }
      return queryClient.fetchQuery(arg1);
    },
  }), [queryClient]);
};

// ─────────────────────────────────────────────────────────────
// Deprecation warnings (dev only)
// ─────────────────────────────────────────────────────────────
const isDev = process.env.NODE_ENV === 'development';

const warn = (hook: string, option: string, hint: string) => {
  if (isDev) {
    console.warn(`[useApi] "${option}" in ${hook} is deprecated. ${hint}`);
  }
};

// ─────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────
type LegacyCallbacks<TData, TError> = {
  /** @deprecated Use returned `data` + `isSuccess` instead */
  onSuccess?: (data: TData) => void;
  /** @deprecated Use returned `error` + `isError` instead */
  onError?: (error: TError) => void;
  /** @deprecated Use `isSuccess`/`isError` states instead */
  onSettled?: (data: TData | undefined, error: TError | null) => void;
  /** @deprecated Use `gcTime` instead */
  cacheTime?: number;
  /** @deprecated Use `placeholderData: keepPreviousData` instead */
  keepPreviousData?: boolean;
};

// ─────────────────────────────────────────────────────────────
// useQuery
// ─────────────────────────────────────────────────────────────
type QueryOptions<TData, TError, TKey extends QueryKey> =
  Omit<UseQueryOptions<TData, TError, TData, TKey>, 'queryKey' | 'queryFn' | 'refetchInterval'> &
  LegacyCallbacks<TData, TError> & {
    /** V5 changed callback from (data) => number to (query) => number. We transform it automatically. */
    refetchInterval?: number | false | ((data: TData | undefined, query?: any) => number | false);
  };

type QueryResult<TData, TError> = Omit<UseQueryResult<TData, TError>, 'status'> & {
  status: 'idle' | 'loading' | 'error' | 'success'; // Map v5 'pending' to v3 'loading'
  isLoading: boolean;
  /** @deprecated Use isPlaceholderData instead */
  isPreviousData?: boolean;
};

// Detect object-style call: useQuery({ queryKey, queryFn })
const isObjectCall = (arg: unknown): arg is { queryKey: QueryKey; queryFn: QueryFunction } =>
  typeof arg === 'object' && arg !== null && 'queryKey' in arg && 'queryFn' in arg;

/**
 * useQuery - Supports both styles:
 *   useQuery({ queryKey, queryFn, ...options })
 *   useQuery(queryKey, queryFn, options?)
 */
export function useQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  options: QueryOptions<TData, TError, TKey> & { queryKey: TKey; queryFn: QueryFunction<TData, TKey> },
): QueryResult<TData, TError>;

export function useQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  queryKey: TKey | string,
  queryFn: QueryFunction<TData, TKey>,
  options?: QueryOptions<TData, TError, TKey>,
): QueryResult<TData, TError>;

export function useQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  arg1: TKey | string | (QueryOptions<TData, TError, TKey> & { queryKey: TKey; queryFn: QueryFunction<TData, TKey> }),
  arg2?: QueryFunction<TData, TKey>,
  arg3?: QueryOptions<TData, TError, TKey>,
): QueryResult<TData, TError> {
  // Parse arguments
  let queryKey: TKey;
  let queryFn: QueryFunction<TData, TKey>;
  let options: QueryOptions<TData, TError, TKey>;

  if (isObjectCall(arg1)) {
    const { queryKey: key, queryFn: fn, ...rest } = arg1;
    queryKey = key as TKey;
    queryFn = fn as QueryFunction<TData, TKey>;
    options = rest as QueryOptions<TData, TError, TKey>;
  } else if (arg2) {
    queryKey = arg1 as TKey;
    queryFn = arg2;
    options = arg3 || {};
  } else {
    throw new Error('useQuery: Invalid arguments');
  }

  // Extract legacy options
  const { onSuccess, onError, onSettled, cacheTime, keepPreviousData: keepPrev, refetchInterval, ...restOptions } = options;

  // Deprecation warnings
  if (onSuccess) warn('useQuery', 'onSuccess', 'Use returned data + isSuccess instead.');
  if (onError) warn('useQuery', 'onError', 'Use returned error + isError instead.');
  if (onSettled) warn('useQuery', 'onSettled', 'Use isSuccess/isError states instead.');
  if (cacheTime !== undefined) warn('useQuery', 'cacheTime', 'Use "gcTime" instead.');
  if (keepPrev) warn('useQuery', 'keepPreviousData', 'Use "placeholderData: keepPreviousData" instead.');

  // Track previous values for callback deduplication
  const prevData = useRef<TData | undefined>(undefined);
  const prevError = useRef<TError | null>(null);

  // Transform refetchInterval callback signature (v5 changed from (data) => number to (query) => number)
  let transformedRefetchInterval: number | false | ((query: any) => number | false) | undefined;
  if (typeof refetchInterval === 'function') {
    transformedRefetchInterval = (query: any) => refetchInterval(query.state.data, query);
  } else {
    transformedRefetchInterval = refetchInterval;
  }

  // Call the real hook
  const result = tanstackUseQuery<TData, TError, TData, TKey>({
    ...restOptions,
    queryKey: toArrayKey(queryKey) as TKey,
    queryFn,
    ...(cacheTime !== undefined && { gcTime: cacheTime }),
    ...(keepPrev && { placeholderData: tanstackKeepPreviousData }),
    ...(transformedRefetchInterval !== undefined && { refetchInterval: transformedRefetchInterval }),
  });

  // onSuccess callback
  useEffect(() => {
    if (onSuccess && result.isSuccess && result.data !== undefined && result.data !== prevData.current) {
      onSuccess(result.data);
      prevData.current = result.data;
    }
  }, [result.data, result.isSuccess, onSuccess]);

  // onError callback
  useEffect(() => {
    if (onError && result.isError && result.error && result.error !== prevError.current) {
      onError(result.error);
      prevError.current = result.error;
    }
  }, [result.error, result.isError, onError]);

  // onSettled callback
  useEffect(() => {
    if (onSettled && !result.isPending && !result.isFetching) {
      if (result.data !== prevData.current || result.error !== prevError.current) {
        onSettled(result.data, result.error);
      }
    }
  }, [result.data, result.error, result.isPending, result.isFetching, onSettled]);

  return {
    ...result,
    status: result.status === 'pending' ? 'loading' : result.status, // Map v5 'pending' to v3 'loading'
    isLoading: result.isPending && result.isFetching,
    isPreviousData: result.isPlaceholderData, // Map deprecated prop
  } as QueryResult<TData, TError>;
}

// ─────────────────────────────────────────────────────────────
// useMutation
// ─────────────────────────────────────────────────────────────
type MutationOptions<TData, TError, TVariables, TContext> =
  Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> & {
    /** @deprecated Use `gcTime` instead */
    cacheTime?: number;
  };

type MutationResult<TData, TError, TVariables, TContext> =
  Omit<UseMutationResult<TData, TError, TVariables, TContext>, 'status'> & {
    status: 'idle' | 'loading' | 'error' | 'success'; // Map v5 'pending' to v3 'loading'
    isLoading: boolean;
  };

// Detect object-style call: useMutation({ mutationFn })
const isMutationObjectCall = (arg: unknown): arg is { mutationFn: Function } =>
  typeof arg === 'object' && arg !== null && 'mutationFn' in arg;

/**
 * useMutation - Supports both styles:
 *   useMutation({ mutationFn, ...options })
 *   useMutation(mutationFn, options?)
 */
export function useMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: MutationOptions<TData, TError, TVariables, TContext> & { mutationFn: (v: TVariables) => Promise<TData> },
): MutationResult<TData, TError, TVariables, TContext>;

export function useMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: MutationOptions<TData, TError, TVariables, TContext>,
): MutationResult<TData, TError, TVariables, TContext>;

export function useMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  arg1: ((v: TVariables) => Promise<TData>) | (MutationOptions<TData, TError, TVariables, TContext> & { mutationFn: (v: TVariables) => Promise<TData> }),
  arg2?: MutationOptions<TData, TError, TVariables, TContext>,
): MutationResult<TData, TError, TVariables, TContext> {
  // Parse arguments
  let mutationFn: (v: TVariables) => Promise<TData>;
  let options: MutationOptions<TData, TError, TVariables, TContext>;

  if (isMutationObjectCall(arg1)) {
    const { mutationFn: fn, ...rest } = arg1;
    mutationFn = fn as (v: TVariables) => Promise<TData>;
    options = rest;
  } else if (typeof arg1 === 'function') {
    mutationFn = arg1;
    options = arg2 || {};
  } else {
    throw new Error('useMutation: Invalid arguments');
  }

  const { cacheTime, ...restOptions } = options;

  if (cacheTime !== undefined) warn('useMutation', 'cacheTime', 'Use "gcTime" instead.');

  const result = tanstackUseMutation<TData, TError, TVariables, TContext>({
    ...restOptions,
    mutationFn,
    ...(cacheTime !== undefined && { gcTime: cacheTime }),
  });

  return {
    ...result,
    status: result.status === 'pending' ? 'loading' : result.status, // Map v5 'pending' to v3 'loading'
    isLoading: result.isPending,
  };
}

// ─────────────────────────────────────────────────────────────
// useQueries
// ─────────────────────────────────────────────────────────────
type QueriesConfig = {
  queryKey: QueryKey;
  queryFn: QueryFunction;
  /** @deprecated Use `gcTime` instead */
  cacheTime?: number;
  /** @deprecated Use `placeholderData: keepPreviousData` instead */
  keepPreviousData?: boolean;
  /** @deprecated Use returned `data` + `isSuccess` instead */
  onSuccess?: Function;
  /** @deprecated Use returned `error` + `isError` instead */
  onError?: Function;
  /** @deprecated Use `isSuccess`/`isError` states instead */
  onSettled?: Function;
  [key: string]: unknown;
};

/**
 * useQueries - Run multiple queries in parallel
 *   useQueries([{ queryKey, queryFn }, ...])
 *   useQueries({ queries: [...] })
 */
export function useQueries(
  queriesOrOptions: QueriesConfig[] | { queries: QueriesConfig[] },
): Array<UseQueryResult & { isLoading: boolean }> {
  const queries = Array.isArray(queriesOrOptions) ? queriesOrOptions : queriesOrOptions.queries;

  // Deprecation warnings (once per call)
  if (isDev) {
    if (queries.some(q => q.onSuccess)) warn('useQueries', 'onSuccess', 'Use returned data + isSuccess instead.');
    if (queries.some(q => q.onError)) warn('useQueries', 'onError', 'Use returned error + isError instead.');
    if (queries.some(q => q.onSettled)) warn('useQueries', 'onSettled', 'Use isSuccess/isError states instead.');
    if (queries.some(q => q.cacheTime !== undefined)) warn('useQueries', 'cacheTime', 'Use "gcTime" instead.');
    if (queries.some(q => q.keepPreviousData)) warn('useQueries', 'keepPreviousData', 'Use "placeholderData: keepPreviousData" instead.');
  }

  // Transform queries
  const transformed = queries.map(({ queryKey, queryFn, cacheTime, keepPreviousData: keepPrev, onSuccess, onError, onSettled, ...rest }) => ({
    ...rest,
    queryKey: toArrayKey(queryKey),
    queryFn,
    ...(cacheTime !== undefined && { gcTime: cacheTime }),
    ...(keepPrev && { placeholderData: tanstackKeepPreviousData }),
  }));

  const results = tanstackUseQueries({ queries: transformed as any });

  return results.map((result: any) => ({
    ...result,
    isLoading: result.isPending && result.isFetching,
    isPreviousData: result.isPlaceholderData, // Map deprecated prop
  }));
}

// ─────────────────────────────────────────────────────────────
// useInfiniteQuery
// ─────────────────────────────────────────────────────────────
type InfiniteQueryOptions<TData, TError, TKey extends QueryKey> =
  Partial<Omit<UseInfiniteQueryOptions<TData, TError, TData, TKey>, 'queryKey' | 'queryFn' | 'initialPageParam'>> &
  LegacyCallbacks<TData, TError> & {
    /** Optional in wrapper - we extract from queryFn default */
    initialPageParam?: unknown;
    /** Required in v5 but we make it optional for backward compatibility */
    getNextPageParam?: UseInfiniteQueryOptions<TData, TError, TData, TKey>['getNextPageParam'];
    /** Optional in v5 */
    getPreviousPageParam?: UseInfiniteQueryOptions<TData, TError, TData, TKey>['getPreviousPageParam'];
  };

type InfiniteQueryResult<TData, TError> = UseInfiniteQueryResult<TData, TError> & {
  isLoading: boolean;
  /** @deprecated Use isPlaceholderData instead */
  isPreviousData?: boolean;
};

// Detect object-style call: useInfiniteQuery({ queryKey, queryFn })
const isInfiniteObjectCall = (arg: unknown): arg is { queryKey: QueryKey; queryFn: QueryFunction } =>
  typeof arg === 'object' && arg !== null && 'queryKey' in arg && 'queryFn' in arg;

// Extract default pageParam from queryFn: ({ pageParam = 0 }) => ...
const extractInitialPageParam = (queryFn: Function): unknown => {
  const fnString = queryFn.toString();
  const match = fnString.match(/pageParam\s*=\s*([^,})\s]+)/);
  if (match) {
    const defaultValue = match[1].trim();
    // Try to parse as number, boolean, or keep as string
    if (defaultValue === 'true') return true;
    if (defaultValue === 'false') return false;
    if (defaultValue === 'null') return null;
    if (defaultValue === 'undefined') return undefined;
    if (/^-?\d+$/.test(defaultValue)) return parseInt(defaultValue, 10);
    if (/^-?\d*\.\d+$/.test(defaultValue)) return parseFloat(defaultValue);
    // Remove quotes if string literal
    return defaultValue.replace(/^["']|["']$/g, '');
  }
  return 0; // Sensible default
};

/**
 * useInfiniteQuery - Supports both styles and extracts initialPageParam:
 *   useInfiniteQuery({ queryKey, queryFn, ...options })
 *   useInfiniteQuery(queryKey, queryFn, options?)
 *
 * V5 Breaking Changes Handled:
 * - Extracts pageParam default from queryFn and moves to initialPageParam
 * - Supports 3-arg style (removed in v5)
 * - Adds onSuccess/onError/cacheTime compat (removed in v5)
 * - Maps keepPreviousData → placeholderData
 * - Maps isPreviousData → isPlaceholderData
 */
export function useInfiniteQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  options: InfiniteQueryOptions<TData, TError, TKey> & { queryKey: TKey; queryFn: QueryFunction<TData, TKey> },
): InfiniteQueryResult<TData, TError>;

export function useInfiniteQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  queryKey: TKey | string,
  queryFn: QueryFunction<TData, TKey>,
  options?: InfiniteQueryOptions<TData, TError, TKey>,
): InfiniteQueryResult<TData, TError>;

export function useInfiniteQuery<TData = unknown, TError = Error, TKey extends QueryKey = QueryKey>(
  arg1: TKey | string | (InfiniteQueryOptions<TData, TError, TKey> & { queryKey: TKey; queryFn: QueryFunction<TData, TKey> }),
  arg2?: QueryFunction<TData, TKey>,
  arg3?: InfiniteQueryOptions<TData, TError, TKey>,
): InfiniteQueryResult<TData, TError> {
  // Parse arguments
  let queryKey: TKey;
  let queryFn: QueryFunction<TData, TKey>;
  let options: InfiniteQueryOptions<TData, TError, TKey>;

  if (isInfiniteObjectCall(arg1)) {
    const { queryKey: key, queryFn: fn, ...rest } = arg1;
    queryKey = key as TKey;
    queryFn = fn as QueryFunction<TData, TKey>;
    options = rest as InfiniteQueryOptions<TData, TError, TKey>;
  } else if (arg2) {
    queryKey = arg1 as TKey;
    queryFn = arg2;
    options = arg3 || {};
  } else {
    throw new Error('useInfiniteQuery: Invalid arguments');
  }

  // Extract legacy options
  const { onSuccess, onError, onSettled, cacheTime, keepPreviousData: keepPrev, initialPageParam, ...restOptions } = options;

  // Deprecation warnings
  if (onSuccess) warn('useInfiniteQuery', 'onSuccess', 'Use returned data + isSuccess instead.');
  if (onError) warn('useInfiniteQuery', 'onError', 'Use returned error + isError instead.');
  if (onSettled) warn('useInfiniteQuery', 'onSettled', 'Use isSuccess/isError states instead.');
  if (cacheTime !== undefined) warn('useInfiniteQuery', 'cacheTime', 'Use "gcTime" instead.');
  if (keepPrev) warn('useInfiniteQuery', 'keepPreviousData', 'Use "placeholderData: keepPreviousData" instead.');

  // Extract initialPageParam from queryFn if not provided
  const finalInitialPageParam = initialPageParam !== undefined ? initialPageParam : extractInitialPageParam(queryFn);

  // Track previous values for callback deduplication
  const prevData = useRef<TData | undefined>(undefined);
  const prevError = useRef<TError | null>(null);

  // Call the real hook
  const result = tanstackUseInfiniteQuery<TData, TError, TData, TKey>({
    ...restOptions,
    queryKey: toArrayKey(queryKey) as TKey,
    queryFn,
    initialPageParam: finalInitialPageParam,
    ...(cacheTime !== undefined && { gcTime: cacheTime }),
    ...(keepPrev && { placeholderData: tanstackKeepPreviousData }),
  } as any);

  // onSuccess callback
  useEffect(() => {
    if (onSuccess && result.isSuccess && result.data !== undefined && result.data !== prevData.current) {
      onSuccess(result.data);
      prevData.current = result.data;
    }
  }, [result.data, result.isSuccess, onSuccess]);

  // onError callback
  useEffect(() => {
    if (onError && result.isError && result.error && result.error !== prevError.current) {
      onError(result.error);
      prevError.current = result.error;
    }
  }, [result.error, result.isError, onError]);

  // onSettled callback
  useEffect(() => {
    if (onSettled && !result.isPending && !result.isFetching) {
      if (result.data !== prevData.current || result.error !== prevError.current) {
        onSettled(result.data, result.error);
      }
    }
  }, [result.data, result.error, result.isPending, result.isFetching, onSettled]);

  return {
    ...result,
    isLoading: result.isPending && result.isFetching,
    isPreviousData: result.isPlaceholderData, // Map deprecated prop
  } as InfiniteQueryResult<TData, TError>;
}
